import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface TableSkeletonProps {
  columns?: number;
  rows?: number;
  hasCheckbox?: boolean;
  hasActions?: boolean;
}

/**
 * Skeleton loading state for data tables
 * Displays animated placeholder rows while data is loading
 */
export function TableSkeleton({
  columns = 5,
  rows = 10,
  hasCheckbox = true,
  hasActions = true,
}: TableSkeletonProps) {
  const totalColumns = columns + (hasCheckbox ? 1 : 0) + (hasActions ? 1 : 0);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {hasCheckbox && (
            <TableHead className="w-12">
              <Skeleton className="h-4 w-4" />
            </TableHead>
          )}
          {Array.from({ length: columns }).map((_, i) => (
            <TableHead key={i}>
              <Skeleton className="h-4 w-20" />
            </TableHead>
          ))}
          {hasActions && (
            <TableHead className="text-right">
              <Skeleton className="h-4 w-16 ml-auto" />
            </TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <TableRow key={rowIndex}>
            {hasCheckbox && (
              <TableCell>
                <Skeleton className="h-4 w-4" />
              </TableCell>
            )}
            {Array.from({ length: columns }).map((_, colIndex) => (
              <TableCell key={colIndex}>
                <Skeleton
                  className={`h-4 ${getColumnWidth(colIndex, columns)}`}
                />
              </TableCell>
            ))}
            {hasActions && (
              <TableCell className="text-right">
                <Skeleton className="h-8 w-8 rounded-full ml-auto" />
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// Vary skeleton widths for more realistic appearance
function getColumnWidth(index: number, total: number): string {
  // First column (usually date) - fixed width
  if (index === 0) return "w-24";
  // Second column (usually description) - wide
  if (index === 1) return "w-48";
  // Amount column (usually second to last)
  if (index === total - 2) return "w-20";
  // Other columns - medium
  return "w-28";
}

/**
 * Card skeleton for mobile table views
 */
export function MobileTableCardSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="space-y-4">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="border rounded-lg p-4 space-y-3">
          <div className="flex justify-between items-start">
            <div className="space-y-2">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-4 w-24" />
            </div>
            <Skeleton className="h-6 w-20" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-6 w-16 rounded-full" />
            <Skeleton className="h-6 w-20 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}
